define((require) => {
    "use strict";

    const _               = require("underscore");
    const Matrix          = require("shared/vendor/Matrix");
    const Transformable   = require("ui/experience/widgets/transformations/Transformable");

    /**
     * An TGroup object inherits from the abstract class {@link Transformable}.
     * It is similar to an {@link TElement} object, but without an actual dom element.
     * This is mainly used in multiple-selection mode.
     *
     * @constructor
     */
    function TGroup(members) {
        this.members = members || [];

        this.width = 0;
        this.height = 0;

        this.matrix = new Matrix();
        this.startMatrix = new Matrix();
        this.endMatrix = new Matrix();
    }

    const abstractObj = new Transformable();

    TGroup.prototype = _.extend({}, abstractObj, {

        /**
         * add a member to the target group
         * @param t a transformable object
         */
        add(t) {
            this.members.push(t);

            // update matrix and control
            this.matrix = this.getMatrix();
            this.draw();
        },

        /**
         * remove a member from the target group
         * @param t a transformable object
         */
        remove(t) {
            const index = _.indexOf(this.members, t);
            if (index > -1) {
                this.members.splice(index, 1);
            }

            // update matrix and control
            this.matrix = this.getMatrix();
            this.draw();
        },

        getControlsConf() {
            // Determine whether `edgeTranslate` should be enabled for `this` group; `true` if any
            // of the contained elements have `edgeTranslate` enabled.
            const edgeTranslate = !!_.find(this.members, (element) => element.getControlsConf().edgeTranslate);

            return {
                scaleX: false,
                scaleY: false,
                scaleUniform: false,
                rotate: false,
                rotateScale: false,
                edgeTranslate
            };
        },

        /**
         * Transform the whole group. For the elements, it delegates to its own transform method
         *
         * @param preMatrix
         * @param postMatrix
         * @param regU
         * @param regV
         */
        transform(preMatrix, postMatrix, regU, regV) {
            abstractObj.transform.apply(this, arguments);

            this.members.forEach((member) => {
                member.transform(preMatrix, postMatrix, regU, regV);
            });
        },

        translate(x, y) {
            abstractObj.transform.apply(this, arguments);

            _.invoke(this.members, "translate", x, y);
        },

        commit() {
            abstractObj.commit.apply(this);

            _.invoke(this.members, "commit");
        },

        setTransparency(opacity) {
            _.invoke(this.members, "setTransparency", opacity);
        },

        updateFromTarget() {
            abstractObj.updateFromTarget.apply(this);

            _.invoke(this.members, "updateFromTarget");
        },

        /**
         * Check if the target group has the TElement object
         *
         * @param t a transformable object
         * @returns {boolean} true if yes, false otherwise
         */
        has(t) {
            return _.indexOf(this.members, t) > -1;
        },

        /**
         * Get the width dimension that this group takes up
         * @returns {int} the width
         */
        getWidth() {
            const xs = this._getAllAxis("x");
            return _.max(xs) - _.min(xs);
        },

        /**
         * Get the height dimension that this group takes up
         * @returns {int} the height
         */
        getHeight() {
            const ys = this._getAllAxis("y");
            return _.max(ys) - _.min(ys);
        },

        /**
         * Get the matrix of this group
         * @returns {*}
         */
        getMatrix() {
            const x = _.min(this._getAllAxis("x"));
            const y = _.min(this._getAllAxis("y"));
            return new Matrix(1, 0, 0, 1, x, y);
        },

        // Currently you can't nest a group inside a group,
        // so this just returns the matrix.
        getMatrixWithParentOffset() {
            return this.getMatrix();
        },

        // get all axis of all members
        _getAllAxis(axis) {
            let union = [];
            this.members.forEach((t) => {
                union = _.union(union, t._getAxis(axis));
            });
            return union;
        },

        setAttr(attr, value) {
            // the group has no element, so only add to the elements inside
            _.invoke(this.members, "setAttr", attr, value);
        }
    });

    return TGroup;
});



// WEBPACK FOOTER //
// ./src/editor/ui/experience/widgets/transformations/TGroup.js